import { type MouseEvent } from 'react';
import { Modal } from 'antd';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { TransferMode } from '@/contexts/chat/scripts/hooks/useFileMessages/file-sdk';
import { useChatContext } from '@/contexts/chat/context';
import { ChatMessagePayloadType } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { useRefs } from '@/scripts/hooks';
import FileItem from '../file-item';
import styles from './index.module.scss';

interface SelectFilesModalProps {
  chat: Chat | undefined;
  open?: boolean;
  onCancel?: () => void;
  files?: File[];
  onOk: (data: File[], mode: TransferMode) => void;
  selectFilesClick: (e: MouseEvent) => void;
  onOpenChange?: (open: boolean) => void;
}

export default function SelectFilesModal({
  open = false,
  onCancel,
  chat,
  files,
  onOk,
  selectFilesClick,
  onOpenChange,
}: SelectFilesModalProps) {
  const [openModal, setOpenModal] = useState(open);
  const [totalFiles, setTotalFiles] = useState<File[]>(files || []);
  const selectFilesModalRef = useRef(`selectFiles${Date.now()}`);

  const handleDelete = (file: File) => {
    const totalFiles_ = totalFiles.filter((item) => item.name !== file.name);
    if (totalFiles_.length === 0) {
      setOpenModal(false);
    } else {
      setTotalFiles(totalFiles_);
    }
  };

  const handleOk = useCallback(() => {
    // TODO amo: TransferMode Select
    onOk?.(totalFiles, TransferMode.SERVER);
    setOpenModal(false);
  }, [onOk, totalFiles]);

  const handleCancel = () => {
    onCancel?.();
    setOpenModal(false);
  };

  const refs = useRefs({ onOpenChange });

  useLayoutEffect(() => {
    if (chat && files?.length) {
      setOpenModal(true);
      setTotalFiles((pre) => [...pre, ...files]);
    }
  }, [chat, files]);

  useLayoutEffect(() => {
    setOpenModal(open);
  }, [open]);

  useLayoutEffect(() => {
    refs.current.onOpenChange?.(openModal);
    if (!openModal) {
      setTotalFiles((prev) => (prev.length ? [] : prev));
    }
  }, [openModal, refs]);

  useLayoutEffect(() => {
    const handleKeyDown = (e: Event) => {
      const keyboardEvent = e as KeyboardEvent;
      if (keyboardEvent.key === 'Enter') {
        handleOk();
      }
    };
    const wrapDom = document.querySelector(`.${selectFilesModalRef.current}`);
    if (openModal) {
      wrapDom?.setAttribute('tabindex', '0');
      wrapDom?.addEventListener('keydown', handleKeyDown);
    } else {
      wrapDom?.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      wrapDom?.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleOk, openModal]);

  return (
    <Modal
      className={styles.selectFilesModal}
      open={openModal}
      onCancel={handleCancel}
      title={<div className={styles.title}>{`Send to ${chat?.name}`}</div>}
      maskClosable={false}
      wrapClassName={selectFilesModalRef.current}
      footer={
        <Flex justify="space-between">
          <Button color="primary" variant="text" onClick={selectFilesClick}>
            Add
          </Button>
          <Flex gap={16}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleOk}
            >{`Send (${totalFiles.length})`}</Button>
          </Flex>
        </Flex>
      }
    >
      <div className={styles.content}>
        {totalFiles.map((file, index) => (
          <FileItem key={index} file={file} handleDelete={handleDelete} />
        ))}
      </div>
    </Modal>
  );
}

export function GlobalPickFilesModal() {
  const {
    fileMessages: { pickFilesUtil, pickInfo, removePickInfo },
    sendMessage,
  } = useChatContext();

  const handleFileOk = (files: File[]) => {
    if (pickInfo) {
      files.forEach((file) => {
        sendMessage({
          chat: pickInfo.chat,
          message: {
            payload_type: file.type.includes('image')
              ? ChatMessagePayloadType.IMAGE
              : ChatMessagePayloadType.FILE,
            data: file,
            payload: '',
          },
        });
      });
    }
  };

  const eventHandlers = useMemo(
    () => pickFilesUtil.getEventHandlers(pickInfo?.options),
    [pickInfo?.options, pickFilesUtil]
  );

  return (
    <SelectFilesModal
      chat={pickInfo?.chat}
      files={pickFilesUtil.files}
      onCancel={pickFilesUtil.reset}
      onOk={handleFileOk}
      selectFilesClick={eventHandlers.click.onClick}
      onOpenChange={(open) => {
        if (!open) {
          removePickInfo();
          pickFilesUtil.reset();
        }
      }}
    ></SelectFilesModal>
  );
}
